/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/member-ordering */
/* eslint-disable no-underscore-dangle */
export interface Burn {
  readonly organization: string;
  readonly organizationUnit: string;
  readonly property: string;
  readonly region: string;
  readonly propertyValue: string;

  /** Returns a string representation of a Burn. */
  toString(): string;
}

export class BurnToken implements Burn {
  private _stringRepresentation: string;
  private _stringRepresentationWithSchema: string;

  private static _segmentsSplitByColons = 6;
  private static _segmentDelimiter = ':';
  private static _scheme = 'burn';
  private static _defaultOrganization = 'billtrust';

  public static SCHEME: string = BurnToken._scheme;
  public static DEFAULT_ORGANIZATION: string = BurnToken._defaultOrganization;

  organization: string;
  organizationUnit: string;
  property: string;
  region: string;
  propertyValue: string;

  constructor(
    organization?: string,
    organizationUnit?: string,
    property?: string,
    region?: string,
    propertyValue?: string,
  ) {
    if (BurnToken.isNullOrWhiteSpace(organization)) {
      this.organization = BurnToken._defaultOrganization;
    } else {
      this.organization = organization.toLowerCase();
    }

    if (BurnToken.isNullOrWhiteSpace(organizationUnit)) {
      throw new Error('organizationUnit must be set.');
    }

    this.organizationUnit = organizationUnit.toLowerCase();

    if (BurnToken.isNullOrWhiteSpace(property)) {
      throw new Error('property must be set.');
    }

    this.property = property.toLowerCase();

    if (BurnToken.isNullOrWhiteSpace(region)) {
      throw new Error('environment must be set.');
    }

    this.region = region.toLowerCase();

    if (BurnToken.isNullOrWhiteSpace(propertyValue)) {
      throw new Error('propertyValue must be set.');
    }

    this.propertyValue = propertyValue.toString().toLowerCase();
  }

  static parse(burnString: string): Burn {
    if (this.isNullOrWhiteSpace(burnString)) {
      throw new Error('burnString must be set.');
    }

    burnString = burnString.toLowerCase();
    const array = burnString.split(this._segmentDelimiter);
    if (array.length !== 6) {
      throw new Error(
        `${burnString} must have ${this._segmentsSplitByColons} segments separated by colons (:). Instead it had ${array.length}`,
      );
    }

    if (array[0].toLowerCase() !== this._scheme) {
      throw new Error(`Invalid Scheme ${array[0]}. Expected ${this._scheme}`);
    }

    const burn: Burn = {
      organization: array[1],
      organizationUnit: array[2],
      property: array[3],
      region: array[4],
      propertyValue: array[5],
    };

    return burn;
  }

  static tryParse(burnString: string): Burn | null {
    try {
      return this.parse(burnString);
    } catch {
      return null;
    }
  }

  static isValid(burnString: string): boolean {
    return this.tryParse(burnString) != null;
  }

  toString(): string {
    if (BurnToken.isNullOrWhiteSpace(this._stringRepresentation)) {
      this._stringRepresentation =
        BurnToken._scheme +
        BurnToken._segmentDelimiter +
        this.toStringWithoutSchema();
    }

    return this._stringRepresentation;
  }

  private toStringWithoutSchema(): string {
    this._stringRepresentationWithSchema =
      this.organization +
      BurnToken._segmentDelimiter +
      this.organizationUnit +
      BurnToken._segmentDelimiter +
      this.property +
      BurnToken._segmentDelimiter +
      this.region +
      BurnToken._segmentDelimiter +
      this.propertyValue;

    return this._stringRepresentationWithSchema;
  }

  private static isNullOrWhiteSpace(str: string | number): boolean {
    if (str === undefined || str === null) {
      return true;
    }

    const valueString = str.toString();
    return /^\s*$/.exec(valueString) !== null;
  }
}
